import type { SpecType } from "@/api/contracts/specifications";
import type { EditorRowKind } from "@/lib/spec-lines-display";

// The rows of a line's ⋮ (#810) — the donor's `ActionMenu` (`SpecEditIsland/
// components/MainContent/Lines/LineMoreActions/ActionMenu.tsx:52-248`) as a
// pure rule: which of its twelve verbs a row offers, in its order, between its
// two rules, and which are drawn held. The component draws what this returns
// and owns nothing about the gates.
//
// **The predicates are the donor's, one for one** (`:52-108`):
//
//   - an *active line row* is a line-level row (`LINE_AND_SELECTION` or
//     `LINE_ONLY`) of a line that is not deleted;
//   - an *active selection row* is a row carrying a selection
//     (`LINE_AND_SELECTION` or `SELECTION_ONLY`) whose selection is not
//     deleted;
//   - *reset* reads the server's changed flags — a single row's line or
//     selection changed **with** its based-on pointer, a header row's line, a
//     selection row's selection — and never the "Show changes from Base"
//     switch.
//
// So a struck line offers its line-level row only its reset, and a struck row
// with nothing to reset offers nothing at all: the donor opens an empty menu
// there (#810 Assumption 6, carried).
//
// **One departure, a defect not carried**: the donor offers `Set as Paid
// Upgrade` on a single row with no selection, whose click throws on
// `selection.id` (`actions.ts:485-535`) and flags nothing. Here the box needs a
// selection to flag.

/** The donor's `ACTIONS` keys (`ActionMenu.tsx:12-25`). */
export type LineMenuAction =
  | "ADD_SELECTION"
  | "EDIT_LINE"
  | "DUPLICATE_LINE"
  | "DELETE_LINE"
  | "CLEAR_LINE"
  | "RESET_LINE"
  | "TOGGLE_PER_PLAN"
  | "TOGGLE_PAID_UPGRADE"
  | "EDIT_SELECTION"
  | "CLEAR_SELECTION"
  | "DELETE_SELECTION"
  | "RESET_SELECTION";

/** Why a row is drawn held: `per-plan`, `Add Selections` on a per-plan line. */
export type LineMenuHold = "per-plan";

export type LineMenuRow =
  | {
      type: "action";
      action: LineMenuAction;
      label: string;
      disabledReason: LineMenuHold | null;
    }
  | { type: "divider" }
  | {
      type: "checkbox";
      action: "TOGGLE_PER_PLAN" | "TOGGLE_PAID_UPGRADE";
      label: string;
      checked: boolean;
    };

export interface LineMenuFacts {
  kind: EditorRowKind;
  line: {
    deleted: boolean;
    perPlan: boolean;
    changedFromBase: boolean;
  };
  /** The row's selection — a header row's is its first shown one (#809). */
  selection:
    | {
        deleted: boolean;
        isPaidUpgrade: boolean;
        changedFromBase: boolean;
      }
    | undefined;
  specType: SpecType;
}

/** The held `Add Selections`' reason, verbatim (`ActionMenu.tsx:137-139`). */
export const PER_PLAN_ADD_SELECTIONS_REASON =
  "To add Selections to an item that are set as 'Per Plan'. Uncheck 'Per Plan' option in the menu.";

/**
 * `RESET_TEXT` (`ActionMenu.tsx:28-31`). A core spec has no key, so its reset
 * has no label — a gate that never opens in practice, a core line having no
 * parent to differ from.
 */
const RESET_TEXT: Record<SpecType, string> = {
  community: "Reset to Base",
  base: "Reset to Core",
  core: "",
};

function action(
  verb: LineMenuAction,
  label: string,
  disabledReason: LineMenuHold | null = null,
): LineMenuRow {
  return { type: "action", action: verb, label, disabledReason };
}

/**
 * `isResetFromBase` (`ActionMenu.tsx:70-86`): the changed-from-base flag alone
 * on every row kind (ADR-0021 decision 2; #981 item 10). The donor's single row
 * also required a based-on pointer, which offered no Reset on flagged rows
 * without one and a no-op Reset on rows whose pointer named their own spec — a
 * named parity break (Q2).
 */
function isResetFromBase({ kind, line, selection }: LineMenuFacts): boolean {
  switch (kind) {
    case "LINE_AND_SELECTION":
      return line.changedFromBase || Boolean(selection?.changedFromBase);
    case "LINE_ONLY":
      return line.changedFromBase;
    case "SELECTION_ONLY":
      return Boolean(selection?.changedFromBase);
  }
}

export function lineMenuRows(facts: LineMenuFacts): LineMenuRow[] {
  const { kind, line, selection, specType } = facts;
  const lineRow = kind === "LINE_AND_SELECTION" || kind === "LINE_ONLY";
  const activeLineRow = lineRow && !line.deleted;
  const selectionOnlyRow = kind === "SELECTION_ONLY";
  const activeSelectionOnlyRow = selectionOnlyRow && !selection?.deleted;
  const activeSelectionRow = selection !== undefined && !selection.deleted && kind !== "LINE_ONLY";
  const reset = isResetFromBase(facts);
  const resetLine = reset && lineRow;
  const resetSelection = reset && selectionOnlyRow && !line.deleted;
  const resetText = RESET_TEXT[specType];

  const rows: LineMenuRow[] = [];
  if (activeLineRow) {
    rows.push(
      line.perPlan
        ? action("ADD_SELECTION", "Add Selections", "per-plan")
        : action("ADD_SELECTION", "Add Selections"),
      action("EDIT_LINE", "Edit Line Details"),
    );
  }
  if (activeSelectionOnlyRow) rows.push(action("EDIT_SELECTION", "Edit Selection Name/Note"));
  // `showSectionTwoDivider && isActiveLineRow` (`:170`): a selection row's
  // section two follows its first row with no rule.
  if (activeLineRow) rows.push({ type: "divider" });
  if (activeLineRow) {
    rows.push(
      action("DUPLICATE_LINE", "Duplicate"),
      action("CLEAR_LINE", "Clear"),
      action("DELETE_LINE", "Delete"),
    );
  }
  if (resetLine) rows.push(action("RESET_LINE", resetText));
  if (activeSelectionOnlyRow) {
    rows.push(action("CLEAR_SELECTION", "Clear"), action("DELETE_SELECTION", "Delete"));
  }
  if (resetSelection) rows.push(action("RESET_SELECTION", resetText));

  const boxes: LineMenuRow[] = [];
  if (activeLineRow) {
    boxes.push({
      type: "checkbox",
      action: "TOGGLE_PER_PLAN",
      label: "Set as Per Plan",
      checked: line.perPlan,
    });
  }
  if (activeSelectionRow) {
    boxes.push({
      type: "checkbox",
      action: "TOGGLE_PAID_UPGRADE",
      label: "Set as Paid Upgrade",
      checked: selection.isPaidUpgrade,
    });
  }
  if (boxes.length > 0) rows.push({ type: "divider" }, ...boxes);
  return rows;
}
